import ExecutionsFilterToolbar from '@components/executions/ExecutionsFilterToolbar';
import WolfButton from '@components/ui/WolfButton';
import GridListSubheader from '@components/GridListSubheader';
import WolfTextLink from '@components/ui/WolfTextLink';
import { graphQlClient } from '@config/graphqlClient';
import ProcessesGraphQL from '@graphql/process.queries';
import ProcessExecutionGraphQL from '@graphql/processExecution.queries';
import WatchlistGraphQL from '@graphql/watchlist.queries';
import AreasGraphQL from '@graphql/area.queries';
import { useLocationQuery } from '@hooks/useLocationQuery';
import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';
import { getProcessByIdRequest } from '@models/process.model';
import { getAreasNameWServProcRequest } from '@models/area.model';
import {
  DateFilterPreset,
  ExecutionFilterState,
  getAssignedByMeRequest,
  getExecutionHistoryPaginatedRequest,
  getMyExecutionsRequest,
  getMyPendingExecutionsCountRequest,
  getMyPendingExecutionsRequest,
  getProcessExecutionsRequest,
  ProcessExecution,
  StepExecution,
  StepExecutionStatus,
} from '@models/processExecution.model';
import { getMyWatchedExecutionsRequest } from '@models/watchlist.model';
import { ConfirmationType } from '@models/step.model';
import { UserRoles } from '@models/user.model';
import {
  Cancel,
  ChatBubbleOutline,
  CheckCircle,
  CheckCircleOutline,
  Error,
  ErrorOutline,
  InfoOutlined,
  KeyboardArrowDown,
  KeyboardArrowUp,
  PendingOutlined,
  PlayArrow,
  RedoOutlined,
  SkipNext,
  ViewList,
  Tab as TabIcon,
} from '@mui/icons-material';
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { setPendingExecutionsCount } from '@redux/reducers/badges.reducer';
import { setUserPreferences } from '@redux/reducers/user.reducer';
import { RootState } from '@redux/store';
import UserGraphQL from '@graphql/user.queries';
import { ExecutionsViewMode, updateUserPreferencesRequest } from '@models/user.model';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

const ExecutionsList: React.FC = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const query = useLocationQuery();
  const processIdFromQuery = query.get('processId');

  // User role and view mode from Redux
  const { role, userId, preferences } = useAppSelector((state: RootState) => state.user);
  const isUserRole = role === UserRoles.USER;
  const useTabsView = preferences?.executionsViewMode !== 'stacked';

  const [executions, setExecutions] = useState<ProcessExecution[]>([]);

  // State for paginated view
  const [paginatedExecutions, setPaginatedExecutions] = useState<ProcessExecution[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  // Separate expanded state for each table section
  const [expandedHistoryExecution, setExpandedHistoryExecution] = useState<string | null>(null);
  const [expandedWatchlistExecution, setExpandedWatchlistExecution] = useState<string | null>(null);
  const [expandedAllExecution, setExpandedAllExecution] = useState<string | null>(null);
  const [expandedPaginatedExecution, setExpandedPaginatedExecution] = useState<string | null>(null);
  // Tab state for admin view
  const [activeTab, setActiveTab] = useState(0);
  // User role view expanded states
  const [expandedUserPendingExecution, setExpandedUserPendingExecution] = useState<string | null>(null);
  const [expandedUserCompletedExecution, setExpandedUserCompletedExecution] = useState<string | null>(null);
  const [processName, setProcessName] = useState<string>('');

  // Filter state for admin view
  const [filterState, setFilterState] = useState<ExecutionFilterState>({
    selectedUsers: [],
    selectedProcesses: [],
    selectedAreas: [],
    selectedServices: [],
    selectedStatuses: [],
    groupingMode: 'process',
    dateFilter: 'today',
  });

  // Filter state for user role view (only date filter)
  const [userDateFilter, setUserDateFilter] = useState<DateFilterPreset>('today');
  const [userCustomDateFrom, setUserCustomDateFrom] = useState<string | undefined>();
  const [userCustomDateTo, setUserCustomDateTo] = useState<string | undefined>();

  // Pagination for user role view
  const [userExecutionsPage, setUserExecutionsPage] = useState(0);
  const [userExecutionsRowsPerPage, setUserExecutionsRowsPerPage] = useState(10);

  // User role: separate state for pending and completed executions
  const [userPendingExecutions, setUserPendingExecutions] = useState<ProcessExecution[]>([]);
  const [userCompletedExecutions, setUserCompletedExecutions] = useState<ProcessExecution[]>([]);

  // My pending executions for admin view (ASSIGNED/IN_PROGRESS)
  const [myPendingExecutions, setMyPendingExecutions] = useState<ProcessExecution[]>([]);

  // My completed executions for admin view
  const [myCompletedExecutions, setMyCompletedExecutions] = useState<ProcessExecution[]>([]);

  // Date filter and pagination for admin's "My Execution History"
  const [myHistoryDateFilter, setMyHistoryDateFilter] = useState<DateFilterPreset>('today');
  const [myHistoryCustomDateFrom, setMyHistoryCustomDateFrom] = useState<string | undefined>();
  const [myHistoryCustomDateTo, setMyHistoryCustomDateTo] = useState<string | undefined>();
  const [myHistoryPage, setMyHistoryPage] = useState(0);
  const [myHistoryRowsPerPage, setMyHistoryRowsPerPage] = useState(10);
  const [myHistoryStatusFilter, setMyHistoryStatusFilter] = useState<string[]>([]);

  // Status filter for My Watchlist
  const [watchlistStatusFilter, setWatchlistStatusFilter] = useState<string[]>([]);
  // Date filter for My Watchlist
  const [watchlistDateFilter, setWatchlistDateFilter] = useState<DateFilterPreset>('today');
  const [watchlistCustomDateFrom, setWatchlistCustomDateFrom] = useState<string | undefined>();
  const [watchlistCustomDateTo, setWatchlistCustomDateTo] = useState<string | undefined>();
  // Pagination for My Watchlist
  const [watchlistPage, setWatchlistPage] = useState(0);
  const [watchlistRowsPerPage, setWatchlistRowsPerPage] = useState(10);

  // Watched pending executions (My Watchlist)
  const [watchedPendingExecutions, setWatchedPendingExecutions] = useState<ProcessExecution[]>([]);
  // Executions assigned by me (My Watchlist)
  const [assignedByMeExecutions, setAssignedByMeExecutions] = useState<ProcessExecution[]>([]);

  // Areas and Services data for filtering
  const [areasData, setAreasData] = useState<any[]>([]);
  const [processToAreaMap, setProcessToAreaMap] = useState<Map<string, string>>(new Map());
  const [processToServiceMap, setProcessToServiceMap] = useState<Map<string, string>>(new Map());

  // Pagination state for "All Executions" table
  const [allExecutionsPage, setAllExecutionsPage] = useState(0);
  const [allExecutionsRowsPerPage, setAllExecutionsRowsPerPage] = useState(10);

  // Sorting state
  type SortField = 'process' | 'status' | 'startedBy' | 'assignedBy' | 'date' | 'duration';
  type SortDirection = 'asc' | 'desc';
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Handler to start execution - navigates to process description page
  const handleStartExecution = (processId: string) => {
    history.push(`/processes/${processId}?execute=true`);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleViewModeChange = async (mode: ExecutionsViewMode) => {
    try {
      const updatedPreferences = {
        ...preferences,
        executionsViewMode: mode,
      };

      const updateResult: updateUserPreferencesRequest = await graphQlClient.request(
        UserGraphQL.mutations.updateUserPreferences,
        {
          input: {
            preferences: updatedPreferences,
          },
        },
      );

      if (updateResult.updateUserPreferences) {
        dispatch(setUserPreferences(updatedPreferences));
      }
    } catch (e: any) {
      console.error('Error updating executions view mode:', e);
    }
  };

  // Helper to properly sort step executions considering containers, iterations, and substeps
  const sortStepExecutions = (steps: StepExecution[] | undefined | null): StepExecution[] => {
    if (!steps) return [];
    return [...steps].sort((a, b) => {
      // For main steps (no iterationNumber), prefer stepSnapshot.order over sequenceOrder
      // stepSnapshot.order contains the original step order from when execution was created
      const isMainA = !a?.iterationNumber;
      const isMainB = !b?.iterationNumber;

      // 1. Get the effective order - use stepSnapshot.order for main steps if available
      const snapshotA = a?.stepSnapshot as { order?: number } | undefined;
      const snapshotB = b?.stepSnapshot as { order?: number } | undefined;
      const orderA = isMainA && snapshotA?.order != null
        ? snapshotA.order
        : (a?.sequenceOrder ?? 0);
      const orderB = isMainB && snapshotB?.order != null
        ? snapshotB.order
        : (b?.sequenceOrder ?? 0);

      if (orderA !== orderB) return orderA - orderB;

      // 2. Sort by iterationNumber (null = main step comes first, then 1, 2, 3...)
      const iterA = a?.iterationNumber ?? 0;
      const iterB = b?.iterationNumber ?? 0;
      if (iterA !== iterB) return iterA - iterB;

      // 3. Sort by substepOrder (order within the iteration)
      const subA = a?.substepOrder ?? 0;
      const subB = b?.substepOrder ?? 0;
      return subA - subB;
    });
  };

  const sortExecutions = useCallback((items: ProcessExecution[]) => {
    return [...items].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'process':
          comparison = (a.processSnapshot?.name || '').localeCompare(b.processSnapshot?.name || '');
          break;
        case 'status':
          comparison = (a.status || '').localeCompare(b.status || '');
          break;
        case 'startedBy':
          const aUser = a.startedByUser?.firstName || a.startedByUser?.lastName || a.startedByUser?.email || '';
          const bUser = b.startedByUser?.firstName || b.startedByUser?.lastName || b.startedByUser?.email || '';
          comparison = aUser.localeCompare(bUser);
          break;
        case 'assignedBy':
          const aAssigned = a.assignedByUser?.firstName || a.assignedByUser?.lastName || a.assignedByUser?.email || '';
          const bAssigned = b.assignedByUser?.firstName || b.assignedByUser?.lastName || b.assignedByUser?.email || '';
          comparison = aAssigned.localeCompare(bAssigned);
          break;
        case 'date':
          comparison = new Date(a.startedAt || 0).getTime() - new Date(b.startedAt || 0).getTime();
          break;
        case 'duration':
          const aDuration = a.startedAt ? (a.completedAt ? new Date(a.completedAt).getTime() : Date.now()) - new Date(a.startedAt).getTime() : 0;
          const bDuration = b.startedAt ? (b.completedAt ? new Date(b.completedAt).getTime() : Date.now()) - new Date(b.startedAt).getTime() : 0;
          comparison = aDuration - bDuration;
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [sortField, sortDirection]);

  const localeProcesses = useTranslation('processes');
  const localeCommon = useTranslation('common');

  useEffect(() => {
    if (processIdFromQuery) {
      getProcessName(processIdFromQuery);
    }
  }, [processIdFromQuery]);

  useEffect(() => {
    setLoading(true);
    if (processIdFromQuery) {
      getPaginatedExecutions(processIdFromQuery);
    } else if (isUserRole) {
      // USER role: fetch pending and completed executions separately
      getUserPendingExecutions();
      getUserCompletedExecutions();
    } else {
      // Admin roles: fetch their own pending and completed executions
      getMyPendingExecutions();
      getMyCompletedExecutions();
    }
  }, [processIdFromQuery, page, rowsPerPage, isUserRole]);

  // Refetch executions when date filter changes (admin view)
  useEffect(() => {
    if (!processIdFromQuery && !isUserRole) {
      getExecutions();
    }
  }, [filterState.dateFilter, filterState.customDateFrom, filterState.customDateTo, isUserRole, processIdFromQuery]);

  // Refetch user completed executions when date filter changes (user view)
  useEffect(() => {
    if (!processIdFromQuery && isUserRole) {
      getUserCompletedExecutions();
    }
  }, [userDateFilter, userCustomDateFrom, userCustomDateTo, isUserRole, processIdFromQuery]);

  // Refetch admin completed executions when date filter changes (admin's My Execution History)
  useEffect(() => {
    if (!processIdFromQuery && !isUserRole) {
      getMyCompletedExecutions();
    }
  }, [myHistoryDateFilter, myHistoryCustomDateFrom, myHistoryCustomDateTo, myHistoryStatusFilter, isUserRole, processIdFromQuery]);

  // Fetch watched executions (My Watchlist) for admin users
  useEffect(() => {
    if (!isUserRole && !processIdFromQuery) {
      getWatchedExecutions();
      getAssignedByMeExecutions();
    }
  }, [isUserRole, processIdFromQuery, watchlistDateFilter, watchlistStatusFilter, watchlistCustomDateFrom, watchlistCustomDateTo]);

  // Fetch areas and services data for filtering
  useEffect(() => {
    if (!isUserRole) {
      fetchAreasAndServices();
    }
  }, [isUserRole]);

  // Refresh pending executions badge count on page load
  useEffect(() => {
    console.log("Refreshing pending executions count");
    const refreshPendingCount = async () => {
      try {
        const countData: getMyPendingExecutionsCountRequest = await graphQlClient.request(
          ProcessExecutionGraphQL.queries.getMyPendingExecutionsCount,
        );
        if (countData?.getMyPendingExecutionsCount !== undefined) {
          dispatch(setPendingExecutionsCount(countData.getMyPendingExecutionsCount));
        }
      } catch (error) {
        console.error('Error refreshing pending executions count:', error);
      }
    };
    refreshPendingCount();
  }, [dispatch]);

  const fetchAreasAndServices = async () => {
    try {
      const response: getAreasNameWServProcRequest = await graphQlClient.request(
        AreasGraphQL.queries.getAreasNameWServProc
      );
      if (response?.getAreas) {
        setAreasData(response.getAreas);

        // Build process -> area and process -> service maps
        const areaMap = new Map<string, string>();
        const serviceMap = new Map<string, string>();

        response.getAreas.forEach((area) => {
          area.services?.forEach((service) => {
            service.processes?.forEach((process) => {
              if (process.processId) {
                areaMap.set(process.processId, area.areaId);
                serviceMap.set(process.processId, service.serviceId);
              }
            });
          });
        });

        setProcessToAreaMap(areaMap);
        setProcessToServiceMap(serviceMap);
      }
    } catch (error) {
      console.error('Error fetching areas and services:', error);
    }
  };

  const getProcessName = async (processId: string) => {
    try {
      const data: getProcessByIdRequest = await graphQlClient.request(ProcessesGraphQL.queries.getProcessById, {
        id: processId,
      });
      if (data?.getProcessById) {
        setProcessName(data.getProcessById.name);
      }
    } catch (error) {
      console.error('Error fetching process name:', error);
    }
  };

  // Fetch user pending executions (ASSIGNED/IN_PROGRESS)
  const getUserPendingExecutions = async () => {
    try {
      const response: getMyPendingExecutionsRequest = await graphQlClient.request(
        ProcessExecutionGraphQL.queries.getMyPendingExecutions,
        {
          pagination: {
            limit: 50,
            offset: 0,
          },
        },
      );

      if (response.getMyPendingExecutions) {
        setUserPendingExecutions(response.getMyPendingExecutions.items);
      }
    } catch (error) {
      console.error('Error fetching user pending executions:', error);
    }
  };

  // Fetch user completed executions (COMPLETED/FAILED/CANCELLED/SKIPPED)
  const getUserCompletedExecutions = async () => {
    setLoading(true);
    try {
      const dateRange = getDateRange(userDateFilter, userCustomDateFrom, userCustomDateTo);

      const response: getMyExecutionsRequest = await graphQlClient.request(
        ProcessExecutionGraphQL.queries.getMyExecutions,
        {
          statusFilter: ['COMPLETED', 'FAILED', 'CANCELLED', 'SKIPPED'],
          dateRange,
          pagination: {
            limit: 1000,
            offset: 0,
          },
        },
      );

      if (response.getMyExecutions) {
        const sorted = response.getMyExecutions.items.sort(
          (a, b) => new Date(b?.startedAt || 0).getTime() - new Date(a?.startedAt || 0).getTime(),
        );
        setUserCompletedExecutions(sorted);
      }
    } catch (error) {
      console.error('Error fetching user completed executions:', error);
      dispatch(
        appendActionMessage({
          message: localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch my pending executions (ASSIGNED/IN_PROGRESS) for admin view
  const getMyPendingExecutions = async () => {
    try {
      const response: getMyPendingExecutionsRequest = await graphQlClient.request(
        ProcessExecutionGraphQL.queries.getMyPendingExecutions,
        {
          pagination: {
            limit: 50,
            offset: 0,
          },
        },
      );

      if (response.getMyPendingExecutions) {
        setMyPendingExecutions(response.getMyPendingExecutions.items);
      }
    } catch (error) {
      console.error('Error fetching my pending executions:', error);
    }
  };

  // Fetch my executions for admin view (all statuses or filtered)
  const getMyCompletedExecutions = async () => {
    try {
      const dateRange = getDateRange(myHistoryDateFilter, myHistoryCustomDateFrom, myHistoryCustomDateTo);

      const variables: any = {
        dateRange,
        pagination: {
          limit: 1000,
          offset: 0,
        },
      };

      // Only add statusFilter if specific statuses are selected
      if (myHistoryStatusFilter.length > 0) {
        variables.statusFilter = myHistoryStatusFilter;
      }

      const response: getMyExecutionsRequest = await graphQlClient.request(
        ProcessExecutionGraphQL.queries.getMyExecutions,
        variables,
      );

      if (response.getMyExecutions) {
        const sorted = response.getMyExecutions.items.sort(
          (a, b) => new Date(b?.startedAt || 0).getTime() - new Date(a?.startedAt || 0).getTime(),
        );
        setMyCompletedExecutions(sorted);
      }
    } catch (error) {
      console.error('Error fetching my executions:', error);
    }
  };

  // Fetch watched executions (from watched processes - all statuses)
  const getWatchedExecutions = async () => {
    try {
      const dateRange = getDateRange(watchlistDateFilter, watchlistCustomDateFrom, watchlistCustomDateTo);
      const response: getMyWatchedExecutionsRequest = await graphQlClient.request(
        WatchlistGraphQL.queries.getMyWatchedExecutions,
        {
          pagination: {
            limit: 100,
            offset: 0,
          },
          dateRange,
          statusFilter: watchlistStatusFilter.length > 0 ? watchlistStatusFilter : undefined,
        },
      );

      if (response.getMyWatchedExecutions) {
        setWatchedPendingExecutions(response.getMyWatchedExecutions.items);
      }
    } catch (error) {
      console.error('Error fetching watched executions:', error);
    }
  };

  // Fetch executions assigned by me
  const getAssignedByMeExecutions = async () => {
    try {
      const dateRange = getDateRange(watchlistDateFilter, watchlistCustomDateFrom, watchlistCustomDateTo);
      const response: getAssignedByMeRequest = await graphQlClient.request(
        ProcessExecutionGraphQL.queries.getAssignedByMe,
        {
          pagination: {
            limit: 50,
            offset: 0,
          },
          statusFilter: watchlistStatusFilter.length > 0 ? watchlistStatusFilter : undefined,
          dateRange,
        },
      );

      if (response.getAssignedByMe) {
        setAssignedByMeExecutions(response.getAssignedByMe.items);
      }
    } catch (error) {
      console.error('Error fetching assigned by me executions:', error);
    }
  };

  const getPaginatedExecutions = async (processId: string) => {
    setLoading(true);
    try {
      const response: getExecutionHistoryPaginatedRequest = await graphQlClient.request(
        ProcessExecutionGraphQL.queries.getExecutionHistoryPaginated,
        {
          processId,
          pagination: {
            limit: rowsPerPage,
            offset: page * rowsPerPage,
          },
        },
      );

      if (response.getExecutionHistoryPaginated) {
        const sorted = response.getExecutionHistoryPaginated.items.sort(
          (a, b) => new Date(b?.startedAt || 0).getTime() - new Date(a?.startedAt || 0).getTime(),
        );
        setPaginatedExecutions(sorted);
        setTotalCount(response.getExecutionHistoryPaginated.pageInfo.totalCount);
      }
    } catch (error) {
      console.error('Error fetching paginated executions:', error);
      dispatch(
        appendActionMessage({
          message: localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  // Helper to get date range based on filter preset
  const getDateRange = (preset: DateFilterPreset | undefined, customFrom?: string, customTo?: string) => {
    const now = DateTime.now();
    const endOfDay = now.endOf('day');

    switch (preset) {
      case 'today':
      case undefined: {
        const startOfDay = now.startOf('day');
        return { startDate: startOfDay.toISO(), endDate: endOfDay.toISO() };
      }

      case 'week': {
        const weekAgo = now.minus({ days: 7 }).startOf('day');
        return { startDate: weekAgo.toISO(), endDate: endOfDay.toISO() };
      }

      case 'month': {
        const monthAgo = now.minus({ months: 1 }).startOf('day');
        return { startDate: monthAgo.toISO(), endDate: endOfDay.toISO() };
      }

      case 'all': {
        // Far past to now to get all executions
        const farPast = DateTime.fromISO('2020-01-01').startOf('day');
        return { startDate: farPast.toISO(), endDate: endOfDay.toISO() };
      }

      case 'custom':
        if (!customFrom && !customTo) return undefined;
        return {
          startDate: customFrom ? DateTime.fromISO(customFrom).startOf('day').toISO() : undefined,
          endDate: customTo ? DateTime.fromISO(customTo).endOf('day').toISO() : undefined,
        };

      default:
        return undefined;
    }
  };

  const getExecutions = async () => {
    setLoading(true);
    try {
      const dateRange = getDateRange(filterState.dateFilter, filterState.customDateFrom, filterState.customDateTo);

      const executionData: getProcessExecutionsRequest = await graphQlClient.request(
        ProcessExecutionGraphQL.queries.getProcessExecutions,
        { dateRange },
      );

      if (executionData?.getProcessExecutions) {
        setExecutions(executionData.getProcessExecutions);
      }
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  // Filter handler for admin view
  const handleFilterChange = useCallback((newFilterState: ExecutionFilterState) => {
    setFilterState(newFilterState);
    setAllExecutionsPage(0); // Reset pagination when filters change
  }, []);

  // Get unique processes from executions for the filter dropdown
  const availableProcesses = useMemo(() => {
    const processMap = new Map<string, string>();
    executions.forEach((execution) => {
      if (execution.processId && execution.processSnapshot?.name) {
        processMap.set(execution.processId, execution.processSnapshot.name);
      }
    });
    return Array.from(processMap.entries()).map(([processId, name]) => ({
      processId,
      name,
    })).sort((a, b) => a.name.localeCompare(b.name));
  }, [executions]);

  const availableAreas = useMemo(() => {
    return areasData
      .filter(area => area.services && area.services.length > 0) // Only areas with services
      .map(area => ({
        areaId: area.areaId,
        name: area.name,
        color: area.color || '#cccccc',
        services: area.services?.map((service: any) => ({
          serviceId: service.serviceId,
          name: service.name,
        })) || [],
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [areasData]);

  const availableServices = useMemo(() => {
    // Count executions per service
    const serviceExecutionCounts = new Map<string, number>();
    executions.forEach((execution) => {
      if (execution.processId) {
        const serviceId = processToServiceMap.get(execution.processId);
        if (serviceId) {
          serviceExecutionCounts.set(serviceId, (serviceExecutionCounts.get(serviceId) || 0) + 1);
        }
      }
    });

    const services: any[] = [];
    areasData.forEach((area: any) => {
      if (area.services && area.services.length > 0) {
        area.services.forEach((service: any) => {
          const count = serviceExecutionCounts.get(service.serviceId) || 0;
          // Only include services with executions
          if (service.serviceId && service.name && count > 0) {
            services.push({
              serviceId: service.serviceId,
              name: service.name,
              areaId: area.areaId,
              areaName: area.name,
              count: count,
            });
          }
        });
      }
    });
    return services.sort((a, b) => {
      // Sort by area name first, then by service name
      const areaCompare = a.areaName.localeCompare(b.areaName);
      return areaCompare !== 0 ? areaCompare : a.name.localeCompare(b.name);
    });
  }, [areasData, executions, processToServiceMap]);

  // Get filtered executions based on filter state (user and process filters - date is server-side)
  const getFilteredExecutions = useCallback(() => {
    let filtered = executions;

    // Filter by selected users (if any)
    if (filterState.selectedUsers.length > 0) {
      const selectedEmails = filterState.selectedUsers.map(u => u.email);
      filtered = filtered.filter((execution) =>
        execution.startedByUser?.email && selectedEmails.includes(execution.startedByUser.email)
      );
    }

    // Filter by selected processes (if any)
    if (filterState.selectedProcesses.length > 0) {
      const selectedProcessIds = filterState.selectedProcesses.map(p => p.processId);
      filtered = filtered.filter((execution) =>
        execution.processId && selectedProcessIds.includes(execution.processId)
      );
    }

    // Filter by selected areas (if any)
    if (filterState.selectedAreas.length > 0) {
      const selectedAreaIds = filterState.selectedAreas.map(a => a.areaId);
      filtered = filtered.filter((execution) => {
        if (!execution.processId) return false;
        const areaId = processToAreaMap.get(execution.processId);
        return areaId && selectedAreaIds.includes(areaId);
      });
    }

    // Filter by selected services (if any)
    if (filterState.selectedServices.length > 0) {
      const selectedServiceIds = filterState.selectedServices.map(s => s.serviceId);
      filtered = filtered.filter((execution) => {
        if (!execution.processId) return false;
        const serviceId = processToServiceMap.get(execution.processId);
        return serviceId && selectedServiceIds.includes(serviceId);
      });
    }

    // Filter by selected statuses (if any)
    if (filterState.selectedStatuses && filterState.selectedStatuses.length > 0) {
      filtered = filtered.filter((execution) =>
        execution.status && filterState.selectedStatuses.includes(execution.status)
      );
    }

    return filtered;
  }, [executions, filterState.selectedUsers, filterState.selectedProcesses, filterState.selectedAreas, filterState.selectedServices, filterState.selectedStatuses, processToAreaMap, processToServiceMap]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'FAILED':
        return 'error';
      case 'CANCELLED':
        return 'default';
      case 'IN_PROGRESS':
        return 'info';
      case 'ASSIGNED':
        return 'default';
      case 'SKIPPED':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStepStatusIcon = (status: StepExecutionStatus) => {
    switch (status) {
      case StepExecutionStatus.COMPLETED:
        return <CheckCircleOutline sx={{ color: 'success.main', fontSize: 16 }} />;
      case StepExecutionStatus.FAILED:
        return <ErrorOutline sx={{ color: 'error.main', fontSize: 16 }} />;
      case StepExecutionStatus.SKIPPED:
        return <RedoOutlined sx={{ color: 'warning.main', fontSize: 16 }} />;
      default:
        return <PendingOutlined sx={{ color: 'text.secondary', fontSize: 16 }} />;
    }
  };

  const calculateDuration = (startedAt: string, completedAt?: string | null) => {
    const start = new Date(startedAt).getTime();
    const end = completedAt ? new Date(completedAt).getTime() : Date.now();
    const durationMs = end - start;

    const hours = Math.floor(durationMs / 3600000);
    const minutes = Math.floor((durationMs % 3600000) / 60000);

    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m`;
    return '< 1m';
  };

  // Format status for human-readable display with locale support
  const formatStatus = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return localeProcesses['completed'];
      case 'FAILED':
        return localeProcesses['failed'];
      case 'CANCELLED':
        return localeProcesses['cancelled'];
      case 'IN_PROGRESS':
        return localeProcesses['in_progress'];
      case 'SKIPPED':
        return localeProcesses['skipped'];
      case 'ASSIGNED':
        return localeProcesses['assigned'];
      default:
        return status;
    }
  };

  // Separate handlers for each table's expand/collapse
  const handleHistoryAccordionChange = (executionId: string) => {
    setExpandedHistoryExecution(expandedHistoryExecution === executionId ? null : executionId);
  };

  const handleWatchlistAccordionChange = (executionId: string) => {
    setExpandedWatchlistExecution(expandedWatchlistExecution === executionId ? null : executionId);
  };

  const handleAllAccordionChange = (executionId: string) => {
    setExpandedAllExecution(expandedAllExecution === executionId ? null : executionId);
  };

  const handlePaginatedAccordionChange = (executionId: string) => {
    setExpandedPaginatedExecution(expandedPaginatedExecution === executionId ? null : executionId);
  };

  // User role view handlers
  const handleUserPendingAccordionChange = (executionId: string) => {
    setExpandedUserPendingExecution(expandedUserPendingExecution === executionId ? null : executionId);
  };

  const handleUserCompletedAccordionChange = (executionId: string) => {
    setExpandedUserCompletedExecution(expandedUserCompletedExecution === executionId ? null : executionId);
  };

  const handleUpdateExecutionStatus = async (executionId: string, status: string) => {
    try {
      await graphQlClient.request(ProcessExecutionGraphQL.mutations.completeProcessExecution, {
        id: executionId,
        status,
      });

      // Refresh the executions list
      if (processIdFromQuery) {
        await getPaginatedExecutions(processIdFromQuery);
      } else if (isUserRole) {
        await getUserPendingExecutions();
        await getUserCompletedExecutions();
      } else {
        await getExecutions();
        // Also refresh My Pending Actions, My Watchlist, and My Execution History for admin
        await getMyPendingExecutions();
        await getMyCompletedExecutions();
        await getWatchedExecutions();
        await getAssignedByMeExecutions();
      }

      // Refresh the pending executions count in the sidebar
      try {
        const countData: getMyPendingExecutionsCountRequest = await graphQlClient.request(
          ProcessExecutionGraphQL.queries.getMyPendingExecutionsCount,
        );
        if (countData?.getMyPendingExecutionsCount !== undefined) {
          dispatch(setPendingExecutionsCount(countData.getMyPendingExecutionsCount));
        }
      } catch (countError) {
        console.error('Error refreshing pending executions count:', countError);
      }

      const statusMessage =
        status === 'COMPLETED'
          ? localeProcesses['executionCompleted']
          : status === 'FAILED'
            ? localeProcesses['executionFailed']
            : status === 'SKIPPED'
              ? localeProcesses['executionSkippedSuccess']
              : localeProcesses['executionCancelled'];

      dispatch(
        appendActionMessage({
          message: statusMessage,
          type: SnackType.SUCCESS,
        }),
      );
    } catch (error) {
      console.error('Error updating execution status:', error);
      dispatch(
        appendActionMessage({
          message: localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    }
  };


  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // User role executions - now coming from separate backend queries
  const actionExecutionsForUser = useMemo(() => {
    return userPendingExecutions;
  }, [userPendingExecutions]);

  const completedExecutionsForUser = useMemo(() => {
    return userCompletedExecutions;
  }, [userCompletedExecutions]);

  const paginatedCompletedExecutions = useMemo(() => {
    const startIndex = userExecutionsPage * userExecutionsRowsPerPage;
    const endIndex = startIndex + userExecutionsRowsPerPage;
    return completedExecutionsForUser.slice(startIndex, endIndex);
  }, [completedExecutionsForUser, userExecutionsPage, userExecutionsRowsPerPage]);

  const handleUserExecutionsChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setUserExecutionsPage(newPage);
  };

  const handleUserExecutionsChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUserExecutionsRowsPerPage(parseInt(event.target.value, 10));
    setUserExecutionsPage(0);
  };

  const handleUserDateFilterChange = (newFilter: DateFilterPreset, customFrom?: string, customTo?: string) => {
    setUserDateFilter(newFilter);
    setUserCustomDateFrom(customFrom);
    setUserCustomDateTo(customTo);
    setUserExecutionsPage(0); // Reset pagination when filter changes
  };

  // Handlers for admin's "My Execution History" date filter and pagination
  const handleMyHistoryDateFilterChange = (newFilter: DateFilterPreset, customFrom?: string, customTo?: string) => {
    setMyHistoryDateFilter(newFilter);
    setMyHistoryCustomDateFrom(customFrom);
    setMyHistoryCustomDateTo(customTo);
    setMyHistoryPage(0); // Reset pagination when filter changes
  };

  const handleMyHistoryChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setMyHistoryPage(newPage);
  };

  const handleMyHistoryChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMyHistoryRowsPerPage(parseInt(event.target.value, 10));
    setMyHistoryPage(0);
  };

  // Filter and paginate "My Execution History" for admin view
  // No frontend filtering needed - backend already filtered by dateRange
  const paginatedMyHistory = useMemo(() => {
    const startIndex = myHistoryPage * myHistoryRowsPerPage;
    const endIndex = startIndex + myHistoryRowsPerPage;
    return myCompletedExecutions.slice(startIndex, endIndex);
  }, [myCompletedExecutions, myHistoryPage, myHistoryRowsPerPage]);

  // Helper to filter executions by date on frontend
  const filterByDateRange = (executions: ProcessExecution[], dateFilter: DateFilterPreset) => {
    const now = DateTime.now();

    return executions.filter(exec => {
      const execDate = exec.startedAt ? DateTime.fromISO(exec.startedAt) : (exec.createdAt ? DateTime.fromISO(exec.createdAt) : null);
      if (!execDate) return true; // Include if no date

      switch (dateFilter) {
        case 'today':
          return execDate >= now.startOf('day');
        case 'week':
          return execDate >= now.minus({ days: 7 }).startOf('day');
        case 'month':
          return execDate >= now.minus({ months: 1 }).startOf('day');
        case 'all':
          return true;
        default:
          return true;
      }
    });
  };

  // Combine My Watchlist (watched processes + assigned by me)
  // Both sources have date & status filters applied server-side
  const filteredWatchlistExecutions = useMemo(() => {
    // Combine both sources and deduplicate by processExecutionId
    const combinedMap = new Map<string, ProcessExecution>();
    watchedPendingExecutions.forEach(exec => {
      combinedMap.set(exec.processExecutionId, exec);
    });
    assignedByMeExecutions.forEach(exec => {
      if (!combinedMap.has(exec.processExecutionId)) {
        combinedMap.set(exec.processExecutionId, exec);
      }
    });
    return Array.from(combinedMap.values());
  }, [watchedPendingExecutions, assignedByMeExecutions]);

  // Paginated watchlist executions
  const paginatedWatchlistExecutions = useMemo(() => {
    const startIndex = watchlistPage * watchlistRowsPerPage;
    const endIndex = startIndex + watchlistRowsPerPage;
    return sortExecutions(filteredWatchlistExecutions).slice(startIndex, endIndex);
  }, [filteredWatchlistExecutions, watchlistPage, watchlistRowsPerPage, sortField, sortDirection]);

  // Watchlist pagination handlers
  const handleWatchlistChangePage = (_event: unknown, newPage: number) => {
    setWatchlistPage(newPage);
  };

  const handleWatchlistChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWatchlistRowsPerPage(parseInt(event.target.value, 10));
    setWatchlistPage(0);
  };

  // Pagination for "All Executions" table (admin view)
  const filteredExecutions = getFilteredExecutions();

  const paginatedFilteredExecutions = useMemo(() => {
    const startIndex = allExecutionsPage * allExecutionsRowsPerPage;
    const endIndex = startIndex + allExecutionsRowsPerPage;
    return sortExecutions(filteredExecutions).slice(startIndex, endIndex);
  }, [filteredExecutions, allExecutionsPage, allExecutionsRowsPerPage, sortField, sortDirection]);

  const handleAllExecutionsChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setAllExecutionsPage(newPage);
  };

  const handleAllExecutionsChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAllExecutionsRowsPerPage(parseInt(event.target.value, 10));
    setAllExecutionsPage(0);
  };

  // Render paginated view for a specific process (same for all roles)
  if (processIdFromQuery) {
    return (
      <Box className={`w-full mb-28`}>
        <Box className="sticky top-0 z-30 bg-backgroundUI">
          <GridListSubheader
            title={`${localeProcesses['executionHistory']}${processName ? `: ${processName}` : ''}`}
            buttonText={null}
            isGridView={false}
            onToggleView={() => {}}
            onDrawerOpen={() => {}}
            icon="process"
            showFavGrid={false}
          />
        </Box>

        <Box className="p-4">
          {/* Back button */}
          <Box className="mb-4">
            <WolfTextLink onClick={() => history.push('/executions')}>
              ← {localeCommon['back']} {localeProcesses['allExecutions'] || 'to All Executions'}
            </WolfTextLink>
          </Box>

          {loading ? (
            <Box className="flex items-center justify-center py-12">
              <CircularProgress />
            </Box>
          ) : paginatedExecutions.length === 0 ? (
            <Box className="flex flex-col items-center justify-center py-8">
              <Typography variant="body1" className="text-gray-500">
                {localeProcesses['noExecutionsYet']}
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={1} className="mb-4 rounded-lg overflow-hidden">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'action.hover' }}>
                    <TableCell sx={{ width: 48 }} />
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>{localeCommon['status']}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>{localeProcesses['startedBy']}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>{localeCommon['date']}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>{localeProcesses['duration']}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedExecutions.map((execution) => (
                    <React.Fragment key={execution.processExecutionId}>
                      <TableRow hover className={execution?.isRedacted ? 'opacity-70' : ''}>
                        <TableCell>
                          {!execution?.isRedacted && execution?.status !== 'ASSIGNED' ? (
                            <IconButton
                              size="small"
                              onClick={() => handlePaginatedAccordionChange(execution?.processExecutionId || '')}
                            >
                              {expandedPaginatedExecution === execution?.processExecutionId ? (
                                <KeyboardArrowUp />
                              ) : (
                                <KeyboardArrowDown />
                              )}
                            </IconButton>
                          ) : (
                            <IconButton size="small" disabled>
                              <KeyboardArrowDown className="opacity-30" />
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box className="flex items-center gap-2">
                            <Chip
                              label={formatStatus(execution?.status || '')}
                              color={getStatusColor(execution?.status || '')}
                              size="small"
                            />
                            {execution?.assignerNote && (
                              <Tooltip title={execution.assignerNote} arrow>
                                <ChatBubbleOutline sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                              </Tooltip>
                            )}
                            {!isUserRole &&
                              !execution?.isRedacted &&
                              execution?.status === 'ASSIGNED' &&
                              execution?.startedByUser?.userId === userId && (
                                <Box className="flex gap-1">
                                  <Tooltip title={localeProcesses['startAssignedExecution']}>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => handleStartExecution(execution.processId)}
                                    >
                                      <PlayArrow fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={localeProcesses['skip']}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleUpdateExecutionStatus(execution?.processExecutionId || '', 'SKIPPED')}
                                    >
                                      <SkipNext fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              )}
                            {!isUserRole &&
                              !execution?.isRedacted &&
                              execution?.status === 'IN_PROGRESS' && (
                                <Box className="flex gap-1">
                                  {execution?.startedByUser?.userId === userId && (
                                    <Tooltip title={localeProcesses['continueExecution']}>
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => handleStartExecution(execution.processId)}
                                      >
                                        <PlayArrow fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  <Tooltip title={localeProcesses['markStuckExecutionAsCompletedTooltip']}>
                                    <IconButton
                                      size="small"
                                      color="success"
                                      onClick={() =>
                                        handleUpdateExecutionStatus(execution?.processExecutionId || '', 'COMPLETED')
                                      }
                                    >
                                      <CheckCircle fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={localeProcesses['markStuckExecutionAsFailedTooltip']}>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() =>
                                        handleUpdateExecutionStatus(execution?.processExecutionId || '', 'FAILED')
                                      }
                                    >
                                      <Error fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={localeProcesses['markStuckExecutionAsCancelledTooltip']}>
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleUpdateExecutionStatus(execution?.processExecutionId || '', 'CANCELLED')
                                      }
                                    >
                                      <Cancel fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {execution?.isRedacted
                            ? <Typography variant="body2" className="text-gray-500 italic">{localeProcesses['executionByAnotherUser']}</Typography>
                            : (execution?.startedByUser?.displayName || execution?.startedByUser?.email || '-')}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {execution?.createdAt
                            ? DateTime.fromISO(execution.createdAt).toFormat('dd-MM-yyyy HH:mm')
                            : '-'}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {execution?.startedAt ? calculateDuration(execution.startedAt, execution?.completedAt) : '-'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                          <Collapse in={!execution?.isRedacted && expandedPaginatedExecution === execution?.processExecutionId} timeout="auto" unmountOnExit>
                            <Box className="py-4">
                              {execution?.stepExecutions && execution?.stepExecutions?.length > 0 ? (
                                <Box className="flex flex-col gap-1">
                                  <Typography variant="caption" className="font-semibold text-gray-500 tracking-wide mb-2">
                                    {localeCommon['allSteps']}
                                  </Typography>
                                  {sortStepExecutions(execution?.stepExecutions)
                                    ?.map((step, index) => (
                                      <Box
                                        key={step?.stepExecutionId}
                                        className="flex flex-col gap-1 py-2 px-3 bg-gray-50 rounded-md border-l-2 border-gray-200"
                                        sx={{
                                          borderLeftColor: step?.status === 'COMPLETED' ? 'success.main' :
                                            step?.status === 'FAILED' ? 'error.main' :
                                            step?.status === 'SKIPPED' ? 'warning.main' : 'grey.300'
                                        }}
                                      >
                                        <Box className="flex items-center gap-2">
                                          <Typography
                                            variant="caption"
                                            className="text-gray-400 font-medium min-w-[20px]"
                                          >
                                            {index + 1}.
                                          </Typography>
                                          <Box className="flex items-center">
                                            {getStepStatusIcon(step?.status || StepExecutionStatus.PENDING)}
                                          </Box>
                                          <Typography variant="body2" className="flex-1 font-medium text-gray-700">
                                            {step?.stepSnapshot?.name || '-'}
                                          </Typography>
                                          <Chip
                                            label={formatStatus(step?.status || '')}
                                            color={getStatusColor(step?.status || '')}
                                            size="small"
                                            sx={{ height: 20, fontSize: '0.7rem' }}
                                          />
                                        </Box>
                                        {step?.errorDetails?.message && (
                                          <Box className="ml-7 mt-1">
                                            <Typography variant="caption" className="text-error">
                                              {localeProcesses['errorDetails']}: {step.errorDetails.message}
                                            </Typography>
                                          </Box>
                                        )}
                                        {step?.stepSnapshot?.stepData?.condition && (
                                          <Box className="ml-7 mt-1">
                                            <Typography variant="caption" className="text-feedbackWarning font-semibold">
                                              {localeProcesses['condition']}: {step.stepSnapshot.stepData.condition}
                                            </Typography>
                                          </Box>
                                        )}
                                        {step?.notes && (
                                          <Box className="ml-7 mt-1">
                                            <Typography variant="caption" className="text-gray-500">
                                              {localeProcesses['notes']}: {step.notes}
                                            </Typography>
                                          </Box>
                                        )}
                                        {step?.confirmationResponse && step?.stepSnapshot?.stepData?.confirmationType && (
                                          <Box className="ml-7 mt-1">
                                            <Typography variant="caption" className="text-primary font-semibold">
                                              {step.stepSnapshot.stepData.confirmationType === ConfirmationType.BOOLEAN
                                                ? `✓ ${localeProcesses['confirmed']}`
                                                : `${step.stepSnapshot.stepData.confirmationQuestion || localeProcesses['confirmationResponse']}: ${step.confirmationResponse}`}
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>
                                    ))}
                                </Box>
                              ) : (
                                <Typography variant="body2" className="text-gray-400 italic">
                                  {localeProcesses['noSteps']}
                                </Typography>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[10, 20, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </Box>
    );
  }

  // USER role: Show paginated view of their own executions
  if (isUserRole) {
    // Use the filtered and paginated executions from top-level hooks
    const actionExecutions = actionExecutionsForUser;
    const completedExecutions = completedExecutionsForUser;

    // Helper function to render expandable row content
    const renderExpandableContent = (execution: ProcessExecution, expandedState: string | null) => (
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
          <Collapse in={expandedState === execution?.processExecutionId} timeout="auto" unmountOnExit>
            <Box className="py-4">
              {execution?.stepExecutions && execution?.stepExecutions?.length > 0 ? (
                <Box className="flex flex-col gap-1">
                  <Typography variant="caption" className="font-semibold text-gray-500 tracking-wide mb-2">
                    {localeCommon['allSteps']}
                  </Typography>
                  {sortStepExecutions(execution?.stepExecutions)
                    ?.map((step, index) => (
                      <Box
                        key={step?.stepExecutionId}
                        className="flex flex-col gap-1 py-2 px-3 bg-gray-50 rounded-md border-l-2 border-gray-200"
                        sx={{
                          borderLeftColor: step?.status === 'COMPLETED' ? 'success.main' :
                            step?.status === 'FAILED' ? 'error.main' :
                            step?.status === 'SKIPPED' ? 'warning.main' : 'grey.300'
                        }}
                      >
                        <Box className="flex items-center gap-2">
                          <Typography
                            variant="caption"
                            className="text-gray-400 font-medium min-w-[20px]"
                          >
                            {index + 1}.
                          </Typography>
                          <Box className="flex items-center">
                            {getStepStatusIcon(step?.status || StepExecutionStatus.PENDING)}
                          </Box>
                          <Typography variant="body2" className="flex-1 font-medium text-gray-700">
                            {step?.stepSnapshot?.name || '-'}
                          </Typography>
                          <Chip
                            label={formatStatus(step?.status || '')}
                            color={getStatusColor(step?.status || '')}
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Box>
                        {step?.errorDetails?.message && (
                          <Box className="ml-7 mt-1">
                            <Typography variant="caption" className="text-error">
                              {localeProcesses['errorDetails']}: {step.errorDetails.message}
                            </Typography>
                          </Box>
                        )}
                        {step?.stepSnapshot?.stepData?.condition && (
                          <Box className="ml-7 mt-1">
                            <Typography variant="caption" className="text-feedbackWarning font-semibold">
                              {localeProcesses['condition']}: {step.stepSnapshot.stepData.condition}
                            </Typography>
                          </Box>
                        )}
                        {step?.notes && (
                          <Box className="ml-7 mt-1">
                            <Typography variant="caption" className="text-gray-500">
                              {localeProcesses['notes']}: {step.notes}
                            </Typography>
                          </Box>
                        )}
                        {step?.confirmationResponse && step?.stepSnapshot?.stepData?.confirmationType && (
                          <Box className="ml-7 mt-1">
                            <Typography variant="caption" className="text-primary font-semibold">
                              {step.stepSnapshot.stepData.confirmationType === ConfirmationType.BOOLEAN
                                ? `✓ ${localeProcesses['confirmed']}`
                                : `${step.stepSnapshot.stepData.confirmationQuestion || localeProcesses['confirmationResponse']}: ${step.confirmationResponse}`}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    ))}
                </Box>
              ) : (
                <Typography variant="body2" className="text-gray-400 italic">
                  {localeProcesses['noSteps']}
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    );

    return (
      <Box className={`w-full mb-28`}>
        <Box className="sticky top-0 z-30 bg-backgroundUI">
          <GridListSubheader
            title={localeProcesses['myExecutions']}
            buttonText={null}
            isGridView={false}
            onToggleView={() => {}}
            onDrawerOpen={() => {}}
            icon="process"
            showFavGrid={false}
          />
        </Box>

        <Box className="p-4">
          {/* Date Filter for My Executions */}
          <ExecutionsFilterToolbar
            filterState={{
              selectedUsers: [],
              selectedProcesses: [],
              selectedAreas: [],
              selectedServices: [],
              selectedStatuses: [],
              groupingMode: 'process',
              dateFilter: userDateFilter,
              customDateFrom: userCustomDateFrom,
              customDateTo: userCustomDateTo,
            }}
            onFilterChange={(newState) => {
              handleUserDateFilterChange(
                newState.dateFilter || 'today',
                newState.customDateFrom,
                newState.customDateTo
              );
            }}
            availableProcesses={[]}
            availableAreas={[]}
            availableServices={[]}
            dateFilterOnly={true}
          />

          {loading ? (
            <Box className="flex items-center justify-center py-12">
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Action Required Table - ASSIGNED and IN_PROGRESS */}
              <Box className="mb-10">
                <Box className="flex items-center gap-1 mb-2">
                  <Typography variant="subtitle1" className="text-primary font-semibold tracking-wide text-sm">
                    {localeProcesses['pendingActions']}
                  </Typography>
                  <Tooltip title={localeProcesses['pendingActionsTooltip']} arrow placement="right">
                    <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Box>
                {actionExecutions.length === 0 ? (
                  <Box className="flex flex-col items-center justify-center py-8 text-center">
                    <Typography variant="body1" className="text-gray-500">
                      {localeProcesses['noExecutionsMatchingFilters']}
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer component={Paper} elevation={2} className="border-l-4 border-primary rounded-lg overflow-hidden">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'grey.200' }}>
                          <TableCell sx={{ width: 48 }} />
                          <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>{localeCommon['process'] || 'Process'}</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>{localeCommon['status']}</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>{localeCommon['action']}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {actionExecutions.map((execution) => (
                          <React.Fragment key={execution.processExecutionId}>
                            <TableRow hover>
                              <TableCell>
                                {execution?.status === 'IN_PROGRESS' ? (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleUserPendingAccordionChange(execution?.processExecutionId || '')}
                                  >
                                    {expandedUserPendingExecution === execution?.processExecutionId ? (
                                      <KeyboardArrowUp />
                                    ) : (
                                      <KeyboardArrowDown />
                                    )}
                                  </IconButton>
                                ) : (
                                  <IconButton size="small" disabled>
                                    <KeyboardArrowDown className="opacity-30" />
                                  </IconButton>
                                )}
                              </TableCell>
                              <TableCell sx={{ maxWidth: 0 }}>
                                <Tooltip title={execution?.processSnapshot?.name || execution?.process?.name || '-'} arrow>
                                  <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    <WolfTextLink onClick={() => history.push(`/processes/${execution.processId}`)}>
                                      {execution?.processSnapshot?.name || execution?.process?.name || '-'}
                                    </WolfTextLink>
                                  </Box>
                                </Tooltip>
                              </TableCell>
                              <TableCell>
                                <Box className="flex items-center gap-2">
                                  <Chip
                                    label={formatStatus(execution?.status || '')}
                                    color={getStatusColor(execution?.status || '')}
                                    size="small"
                                  />
                                  {execution?.assignerNote && (
                                    <Tooltip title={execution.assignerNote} arrow>
                                      <ChatBubbleOutline sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                                    </Tooltip>
                                  )}
                                  {execution?.status === 'IN_PROGRESS' && (
                                      <Box className="flex gap-1">
                                        <Tooltip title={localeProcesses['markStuckExecutionAsCompletedTooltip']}>
                                          <IconButton
                                            size="small"
                                            color="success"
                                            onClick={() =>
                                              handleUpdateExecutionStatus(execution?.processExecutionId || '', 'COMPLETED')
                                            }
                                          >
                                            <CheckCircle fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title={localeProcesses['markStuckExecutionAsFailedTooltip']}>
                                          <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() =>
                                              handleUpdateExecutionStatus(execution?.processExecutionId || '', 'FAILED')
                                            }
                                          >
                                            <Error fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title={localeProcesses['markStuckExecutionAsCancelledTooltip']}>
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              handleUpdateExecutionStatus(execution?.processExecutionId || '', 'CANCELLED')
                                            }
                                          >
                                            <Cancel fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box className="flex gap-4">
                                  <WolfButton
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    startIcon={<PlayArrow />}
                                    onClick={() => handleStartExecution(execution.processId)}
                                  >
                                    {execution?.status === 'ASSIGNED'
                                      ? localeProcesses['startAssignedExecution']
                                      : localeProcesses['continueExecution']}
                                  </WolfButton>
                                  {execution?.status === 'ASSIGNED' && (
                                    <WolfButton
                                      variant="outlined"
                                      color="secondary"
                                      size="small"
                                      startIcon={<SkipNext />}
                                      onClick={() => handleUpdateExecutionStatus(execution?.processExecutionId || '', 'SKIPPED')}
                                    >
                                      {localeProcesses['skip']}
                                    </WolfButton>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                            {execution?.status === 'IN_PROGRESS' && renderExpandableContent(execution, expandedUserPendingExecution)}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>

              {/* Completed Executions Table */}
              <Box>
                <Box className="flex items-center gap-1 mb-2">
                  <Typography variant="subtitle1" className="text-textSecondary font-semibold tracking-wide text-sm">
                    {localeProcesses['myExecutionHistory']}
                  </Typography>
                  <Tooltip title={localeProcesses['myExecutionHistoryTooltip']} arrow placement="right">
                    <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Box>
                {completedExecutions.length === 0 ? (
                  <Box className="flex flex-col items-center justify-center py-8 text-center">
                    <Typography variant="body1" className="text-gray-500">
                      {localeProcesses['noExecutionsMatchingFilters']}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <TableContainer component={Paper} elevation={1} className="mb-4 rounded-lg overflow-hidden">
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: 'action.hover' }}>
                            <TableCell sx={{ width: 48 }} />
                            <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>{localeCommon['process'] || 'Process'}</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>{localeCommon['status']}</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>{localeCommon['date']}</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>{localeProcesses['duration']}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {paginatedCompletedExecutions.map((execution) => (
                            <React.Fragment key={execution.processExecutionId}>
                              <TableRow hover>
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleUserCompletedAccordionChange(execution?.processExecutionId || '')}
                                  >
                                    {expandedUserCompletedExecution === execution?.processExecutionId ? (
                                      <KeyboardArrowUp />
                                    ) : (
                                      <KeyboardArrowDown />
                                    )}
                                  </IconButton>
                                </TableCell>
                                <TableCell sx={{ maxWidth: 0 }}>
                                  <Tooltip title={execution?.processSnapshot?.name || execution?.process?.name || '-'} arrow>
                                    <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      <WolfTextLink onClick={() => history.push(`/processes/${execution.processId}`)}>
                                        {execution?.processSnapshot?.name || execution?.process?.name || '-'}
                                      </WolfTextLink>
                                    </Box>
                                  </Tooltip>
                                </TableCell>
                                <TableCell>
                                  <Box className="flex items-center gap-2">
                                    <Chip
                                      label={formatStatus(execution?.status || '')}
                                      color={getStatusColor(execution?.status || '')}
                                      size="small"
                                    />
                                    {execution?.assignerNote && (
                                      <Tooltip title={execution.assignerNote} arrow>
                                        <ChatBubbleOutline sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                                      </Tooltip>
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                  {execution?.createdAt
                                    ? DateTime.fromISO(execution.createdAt).toFormat('dd-MM-yyyy HH:mm')
                                    : '-'}
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                  {execution?.startedAt
                                    ? calculateDuration(execution.startedAt, execution?.completedAt)
                                    : '-'}
                                </TableCell>
                              </TableRow>
                              {renderExpandableContent(execution, expandedUserCompletedExecution)}
                            </React.Fragment>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      rowsPerPageOptions={[10, 20, 50]}
                      component="div"
                      count={completedExecutions.length}
                      rowsPerPage={userExecutionsRowsPerPage}
                      page={userExecutionsPage}
                      onPageChange={handleUserExecutionsChangePage}
                      onRowsPerPageChange={handleUserExecutionsChangeRowsPerPage}
                    />
                  </>
                )}
              </Box>
            </>
          )}
        </Box>

      </Box>
    );
  }

  // Admin roles: Grouped view with filters
  return (
    <Box className={`w-full mb-28`}>
      <Box className="sticky top-0 z-30 bg-backgroundUI">
        <GridListSubheader
          title={localeProcesses['executionHistory']}
          buttonText={null}
          isGridView={false}
          onToggleView={() => {}}
          onDrawerOpen={() => {}}
          icon="process"
          showFavGrid={false}
        />
      </Box>

      <Box className="p-4">
        {/* View mode toggle and tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }} className="flex justify-between items-center">
          {useTabsView ? (
            <Tabs value={activeTab} onChange={(_e, newValue) => setActiveTab(newValue)}>
              <Tab label={`${localeProcesses['pendingActions']} ${myPendingExecutions.length > 0 ? `(${myPendingExecutions.length})` : ''}`} />
              <Tab label={localeProcesses['myExecutionHistory']} />
              <Tab label={localeProcesses['myWatchlist']} />
              <Tab label={localeProcesses['allExecutions']} />
            </Tabs>
          ) : (
            <Box />
          )}
          <ToggleButtonGroup
            value={useTabsView ? 'tabs' : 'stacked'}
            exclusive
            onChange={(_e, value) => value && handleViewModeChange(value)}
            size="small"
          >
            <ToggleButton value="tabs">
              <Tooltip title={localeProcesses['tabsView']}>
                <TabIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="stacked">
              <Tooltip title={localeProcesses['stackedView']}>
                <ViewList fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Tab 0: My Pending Actions */}
        {((useTabsView && activeTab === 0) || !useTabsView) && (
          <Box>
            <Box className="flex items-center gap-1 mb-2">
              <Typography variant="subtitle1" className="text-primary font-semibold tracking-wide text-sm">
                {localeProcesses['pendingActions']}
              </Typography>
              <Tooltip title={localeProcesses['pendingActionsTooltip']} arrow placement="right">
                <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Box>
            {myPendingExecutions.length === 0 ? (
              <Box className="flex flex-col items-center justify-center py-8">
                <Typography variant="body1" className="text-gray-500">
                  {localeProcesses['noExecutionsMatchingFilters']}
                </Typography>
              </Box>
            ) : (
            <TableContainer component={Paper} elevation={2} className="border-l-4 border-primary rounded-lg overflow-hidden">
              <Table size="small" sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.200' }}>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', width: '40%' }}>{localeCommon['process'] || 'Process'}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', width: '35%' }}>{localeCommon['status']}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', width: '25%' }}>{localeCommon['action']}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {myPendingExecutions.map((execution) => (
                    <TableRow key={execution.processExecutionId} hover>
                      <TableCell sx={{ maxWidth: 0 }}>
                        <Tooltip title={execution?.processSnapshot?.name || execution?.process?.name || '-'} arrow>
                          <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <WolfTextLink onClick={() => history.push(`/processes/${execution.processId}`)}>
                              {execution?.processSnapshot?.name || execution?.process?.name || '-'}
                            </WolfTextLink>
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Box className="flex items-center gap-2">
                          <Chip
                            label={formatStatus(execution?.status || '')}
                            color={getStatusColor(execution?.status || '')}
                            size="small"
                          />
                          {execution?.assignerNote && (
                            <Tooltip title={execution.assignerNote} arrow>
                              <ChatBubbleOutline sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                            </Tooltip>
                          )}
                          {execution?.status === 'IN_PROGRESS' && (
                              <Box className="flex gap-1">
                                <Tooltip title={localeProcesses['markStuckExecutionAsCompletedTooltip']}>
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() =>
                                      handleUpdateExecutionStatus(execution?.processExecutionId || '', 'COMPLETED')
                                    }
                                  >
                                    <CheckCircle fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={localeProcesses['markStuckExecutionAsFailedTooltip']}>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() =>
                                      handleUpdateExecutionStatus(execution?.processExecutionId || '', 'FAILED')
                                    }
                                  >
                                    <Error fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={localeProcesses['markStuckExecutionAsCancelledTooltip']}>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleUpdateExecutionStatus(execution?.processExecutionId || '', 'CANCELLED')
                                    }
                                  >
                                    <Cancel fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box className="flex gap-4">
                          <WolfButton
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<PlayArrow />}
                            onClick={() => handleStartExecution(execution.processId)}
                          >
                            {execution?.status === 'ASSIGNED'
                              ? localeProcesses['startAssignedExecution']
                              : localeProcesses['continueExecution']}
                          </WolfButton>
                          {execution?.status === 'ASSIGNED' && (
                            <WolfButton
                              variant="outlined"
                              color="secondary"
                              size="small"
                              startIcon={<SkipNext />}
                              onClick={() => handleUpdateExecutionStatus(execution?.processExecutionId || '', 'SKIPPED')}
                            >
                              {localeProcesses['skip']}
                            </WolfButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            )}
          </Box>
        )}

        {/* Tab 1: My Execution History */}
        {((useTabsView && activeTab === 1) || !useTabsView) && (
        <Box>
          <Box className="flex items-center gap-1 mb-2">
            <Typography variant="subtitle1" className="text-textSecondary font-semibold tracking-wide text-sm">
              {localeProcesses['myExecutionHistory']}
            </Typography>
            <Tooltip title={localeProcesses['myExecutionHistoryTooltip']} arrow placement="right">
              <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
            </Tooltip>
          </Box>

          {/* Date Filter for My Execution History */}
          <ExecutionsFilterToolbar
            filterState={{
              selectedUsers: [],
              selectedProcesses: [],
              selectedAreas: [],
              selectedServices: [],
              selectedStatuses: [],
              groupingMode: 'process',
              dateFilter: myHistoryDateFilter,
              customDateFrom: myHistoryCustomDateFrom,
              customDateTo: myHistoryCustomDateTo,
            }}
            onFilterChange={(newState) => {
              handleMyHistoryDateFilterChange(
                newState.dateFilter || 'today',
                newState.customDateFrom,
                newState.customDateTo
              );
            }}
            availableProcesses={[]}
            availableAreas={[]}
            availableServices={[]}
            dateFilterOnly={true}
          />

          {/* Status Filter Buttons */}
          <Box className="flex flex-wrap gap-2 mb-3">
            <Chip
              label={localeCommon['all']}
              size="small"
              color={myHistoryStatusFilter.length === 0 ? 'primary' : 'default'}
              onClick={() => setMyHistoryStatusFilter([])}
              sx={{ cursor: 'pointer' }}
            />
            {['ASSIGNED', 'IN_PROGRESS', 'COMPLETED', 'FAILED', 'CANCELLED', 'SKIPPED'].map((status) => (
              <Chip
                key={status}
                label={formatStatus(status)}
                size="small"
                color={myHistoryStatusFilter.includes(status) ? 'primary' : 'default'}
                onClick={() => {
                  if (myHistoryStatusFilter.includes(status)) {
                    setMyHistoryStatusFilter(myHistoryStatusFilter.filter(s => s !== status));
                  } else {
                    setMyHistoryStatusFilter([...myHistoryStatusFilter, status]);
                  }
                }}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>

          {myCompletedExecutions.length === 0 ? (
            <Box className="flex flex-col items-center justify-center py-8">
              <Typography variant="body1" className="text-gray-500">
                {localeProcesses['noExecutionsMatchingFilters']}
              </Typography>
            </Box>
          ) : (
              <>
                <TableContainer component={Paper} elevation={1} className="mb-4 rounded-lg overflow-hidden">
                  <Table size="small" sx={{ tableLayout: 'fixed' }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'grey.200' }}>
                        <TableCell sx={{ width: '3%', padding: '0 4px' }} />
                        <TableCell sx={{ fontWeight: 600, width: '18%' }}>
                          <TableSortLabel
                            active={sortField === 'process'}
                            direction={sortField === 'process' ? sortDirection : 'asc'}
                            onClick={() => handleSort('process')}
                          >
                            {localeCommon['process'] || 'Process'}
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, width: '20%' }}>
                          <TableSortLabel
                            active={sortField === 'status'}
                            direction={sortField === 'status' ? sortDirection : 'asc'}
                            onClick={() => handleSort('status')}
                          >
                            {localeCommon['status']}
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, width: '15%' }}>
                          <TableSortLabel
                            active={sortField === 'startedBy'}
                            direction={sortField === 'startedBy' ? sortDirection : 'asc'}
                            onClick={() => handleSort('startedBy')}
                          >
                            {localeProcesses['startedByAssignedTo']}
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, width: '12%', whiteSpace: 'nowrap' }}>
                          <TableSortLabel
                            active={sortField === 'assignedBy'}
                            direction={sortField === 'assignedBy' ? sortDirection : 'asc'}
                            onClick={() => handleSort('assignedBy')}
                          >
                            {localeProcesses['assignedBy']}
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, width: '12%' }}>
                          <TableSortLabel
                            active={sortField === 'date'}
                            direction={sortField === 'date' ? sortDirection : 'asc'}
                            onClick={() => handleSort('date')}
                          >
                            {localeCommon['date']}
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, width: '8%' }}>
                          <TableSortLabel
                            active={sortField === 'duration'}
                            direction={sortField === 'duration' ? sortDirection : 'asc'}
                            onClick={() => handleSort('duration')}
                          >
                            {localeProcesses['duration']}
                          </TableSortLabel>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortExecutions(paginatedMyHistory).map((execution) => (
                        <React.Fragment key={execution.processExecutionId}>
                          <TableRow hover>
                            <TableCell>
                              {execution?.status !== 'ASSIGNED' ? (
                                <IconButton
                                  size="small"
                                  onClick={() => handleHistoryAccordionChange(execution?.processExecutionId || '')}
                                >
                                  {expandedHistoryExecution === execution?.processExecutionId ? (
                                    <KeyboardArrowUp />
                                  ) : (
                                    <KeyboardArrowDown />
                                  )}
                                </IconButton>
                              ) : (
                                <IconButton size="small" disabled>
                                  <KeyboardArrowDown className="opacity-30" />
                                </IconButton>
                              )}
                            </TableCell>
                            <TableCell sx={{ maxWidth: 0 }}>
                              <Tooltip title={execution?.processSnapshot?.name || execution?.process?.name || '-'} arrow>
                                <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  <WolfTextLink onClick={() => history.push(`/processes/${execution.processId}`)}>
                                    {execution?.processSnapshot?.name || execution?.process?.name || '-'}
                                  </WolfTextLink>
                                </Box>
                              </Tooltip>
                            </TableCell>
                            <TableCell>
                              <Box className="flex items-center gap-2">
                                <Chip
                                  label={formatStatus(execution?.status || '')}
                                  color={getStatusColor(execution?.status || '')}
                                  size="small"
                                />
                                {execution?.assignerNote && (
                                  <Tooltip title={execution.assignerNote} arrow>
                                    <ChatBubbleOutline sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                                  </Tooltip>
                                )}
                                {!execution?.isRedacted &&
                                  execution?.status === 'ASSIGNED' &&
                                  execution?.startedByUser?.userId === userId && (
                                    <Box className="flex gap-1">
                                      <Tooltip title={localeProcesses['startAssignedExecution']}>
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          onClick={() => handleStartExecution(execution.processId)}
                                        >
                                          <PlayArrow fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title={localeProcesses['skip']}>
                                        <IconButton
                                          size="small"
                                          onClick={() => handleUpdateExecutionStatus(execution?.processExecutionId || '', 'SKIPPED')}
                                        >
                                          <SkipNext fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  )}
                                {!execution?.isRedacted &&
                                  execution?.status === 'IN_PROGRESS' && (
                                    <Box className="flex gap-1">
                                      {execution?.startedByUser?.userId === userId && (
                                        <Tooltip title={localeProcesses['continueExecution']}>
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => handleStartExecution(execution.processId)}
                                          >
                                            <PlayArrow fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                      <Tooltip title={localeProcesses['markStuckExecutionAsCompletedTooltip']}>
                                        <IconButton
                                          size="small"
                                          color="success"
                                          onClick={() =>
                                            handleUpdateExecutionStatus(execution?.processExecutionId || '', 'COMPLETED')
                                          }
                                        >
                                          <CheckCircle fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title={localeProcesses['markStuckExecutionAsFailedTooltip']}>
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() =>
                                            handleUpdateExecutionStatus(execution?.processExecutionId || '', 'FAILED')
                                          }
                                        >
                                          <Error fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title={localeProcesses['markStuckExecutionAsCancelledTooltip']}>
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            handleUpdateExecutionStatus(execution?.processExecutionId || '', 'CANCELLED')
                                          }
                                        >
                                          <Cancel fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              {execution?.startedByUser ? (
                                <Tooltip title={execution.startedByUser.email || ''} arrow>
                                  <span>
                                    {execution.startedByUser.firstName || execution.startedByUser.lastName
                                      ? `${execution.startedByUser.firstName || ''} ${execution.startedByUser.lastName || ''}`.trim()
                                      : execution.startedByUser.email || '-'}
                                  </span>
                                </Tooltip>
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              {execution?.assignedByUser ? (
                                <Tooltip title={execution.assignedByUser.email || ''} arrow>
                                  <span>
                                    {execution.assignedByUser.firstName || execution.assignedByUser.lastName
                                      ? `${execution.assignedByUser.firstName || ''} ${execution.assignedByUser.lastName || ''}`.trim()
                                      : execution.assignedByUser.email || '-'}
                                  </span>
                                </Tooltip>
                              ) : '-'}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {execution?.createdAt
                                ? DateTime.fromISO(execution.createdAt).toFormat('dd-MM-yyyy HH:mm')
                                : '-'}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {execution?.startedAt ? calculateDuration(execution.startedAt, execution?.completedAt) : '-'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                              <Collapse in={expandedHistoryExecution === execution?.processExecutionId} timeout="auto" unmountOnExit>
                                <Box className="py-4">
                                  {execution?.stepExecutions && execution?.stepExecutions?.length > 0 ? (
                                    <Box className="flex flex-col gap-1">
                                      <Typography variant="caption" className="font-semibold text-gray-500 tracking-wide mb-2">
                                        {localeCommon['allSteps']}
                                      </Typography>
                                      {sortStepExecutions(execution?.stepExecutions)
                                        ?.map((step, index) => (
                                          <Box
                                            key={step?.stepExecutionId}
                                            className="flex flex-col gap-1 py-2 px-3 bg-gray-50 rounded-md border-l-2 border-gray-200"
                                            sx={{
                                              borderLeftColor: step?.status === 'COMPLETED' ? 'success.main' :
                                                step?.status === 'FAILED' ? 'error.main' :
                                                step?.status === 'SKIPPED' ? 'warning.main' : 'grey.300'
                                            }}
                                          >
                                            <Box className="flex items-center gap-2">
                                              <Typography
                                                variant="caption"
                                                className="text-gray-400 font-medium min-w-[20px]"
                                              >
                                                {index + 1}.
                                              </Typography>
                                              <Box className="flex items-center">
                                                {getStepStatusIcon(step?.status || StepExecutionStatus.PENDING)}
                                              </Box>
                                              <Typography variant="body2" className="flex-1 font-medium text-gray-700">
                                                {step?.stepSnapshot?.name || '-'}
                                              </Typography>
                                              <Chip
                                                label={formatStatus(step?.status || '')}
                                                color={getStatusColor(step?.status || '')}
                                                size="small"
                                                sx={{ height: 20, fontSize: '0.7rem' }}
                                              />
                                            </Box>
                                            {step?.notes && (
                                              <Box className="ml-7 mt-1">
                                                <Typography variant="caption" className="text-gray-500">
                                                  {localeProcesses['notes']}: {step.notes}
                                                </Typography>
                                              </Box>
                                            )}
                                          </Box>
                                        ))}
                                    </Box>
                                  ) : (
                                    <Typography variant="body2" className="text-gray-400 italic">
                                      {localeProcesses['noSteps']}
                                    </Typography>
                                  )}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[10, 20, 50]}
                  component="div"
                  count={myCompletedExecutions.length}
                  rowsPerPage={myHistoryRowsPerPage}
                  page={myHistoryPage}
                  onPageChange={handleMyHistoryChangePage}
                  onRowsPerPageChange={handleMyHistoryChangeRowsPerPage}
                />
            </>
          )}
        </Box>
        )}

        {/* Tab 2: My Watchlist */}
        {((useTabsView && activeTab === 2) || !useTabsView) && (
          <Box>
            <Box className="flex items-center gap-1 mb-2">
              <Typography variant="subtitle1" className="text-textSecondary font-semibold tracking-wide text-sm">
                {localeProcesses['myWatchlist']}
              </Typography>
              <Tooltip title={localeProcesses['myWatchlistTooltip']} arrow placement="right">
                <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Box>

              {/* Date Filter */}
              <ExecutionsFilterToolbar
                filterState={{
                  selectedUsers: [],
                  selectedProcesses: [],
                  selectedAreas: [],
                  selectedServices: [],
                  selectedStatuses: [],
                  groupingMode: 'process',
                  dateFilter: watchlistDateFilter,
                  customDateFrom: watchlistCustomDateFrom,
                  customDateTo: watchlistCustomDateTo,
                }}
                onFilterChange={(newState) => {
                  setWatchlistDateFilter(newState.dateFilter || 'today');
                  setWatchlistCustomDateFrom(newState.customDateFrom);
                  setWatchlistCustomDateTo(newState.customDateTo);
                  setWatchlistPage(0);
                }}
                availableProcesses={[]}
                availableAreas={[]}
                availableServices={[]}
                dateFilterOnly={true}
              />

              {/* Status Filter Buttons */}
              <Box className="flex flex-wrap gap-2 mb-3">
                <Chip
                  label={localeCommon['all']}
                  size="small"
                  color={watchlistStatusFilter.length === 0 ? 'primary' : 'default'}
                  onClick={() => setWatchlistStatusFilter([])}
                  sx={{ cursor: 'pointer' }}
                />
                {['ASSIGNED', 'IN_PROGRESS', 'COMPLETED', 'FAILED', 'CANCELLED', 'SKIPPED'].map((status) => (
                  <Chip
                    key={status}
                    label={formatStatus(status)}
                    size="small"
                    color={watchlistStatusFilter.includes(status) ? 'primary' : 'default'}
                    onClick={() => {
                      if (watchlistStatusFilter.includes(status)) {
                        setWatchlistStatusFilter(watchlistStatusFilter.filter(s => s !== status));
                      } else {
                        setWatchlistStatusFilter([...watchlistStatusFilter, status]);
                      }
                    }}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>

              {filteredWatchlistExecutions.length === 0 ? (
                <Box className="flex flex-col items-center justify-center py-8 text-center">
                  <Typography variant="body1" className="text-gray-500">
                    {localeProcesses['noExecutionsMatchingFilters']}
                  </Typography>
                </Box>
              ) : (
              <>
              <TableContainer component={Paper} elevation={1} className="rounded-lg overflow-hidden">
                <Table size="small" sx={{ tableLayout: 'fixed' }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.200' }}>
                      <TableCell sx={{ width: '3%', padding: '0 4px' }} />
                      <TableCell sx={{ fontWeight: 600, width: '18%' }}>
                        <TableSortLabel
                          active={sortField === 'process'}
                          direction={sortField === 'process' ? sortDirection : 'asc'}
                          onClick={() => handleSort('process')}
                        >
                          {localeCommon['process'] || 'Process'}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '20%' }}>
                        <TableSortLabel
                          active={sortField === 'status'}
                          direction={sortField === 'status' ? sortDirection : 'asc'}
                          onClick={() => handleSort('status')}
                        >
                          {localeCommon['status']}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '15%' }}>
                        <TableSortLabel
                          active={sortField === 'startedBy'}
                          direction={sortField === 'startedBy' ? sortDirection : 'asc'}
                          onClick={() => handleSort('startedBy')}
                        >
                          {localeProcesses['startedByAssignedTo']}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '12%', whiteSpace: 'nowrap' }}>
                        <TableSortLabel
                          active={sortField === 'assignedBy'}
                          direction={sortField === 'assignedBy' ? sortDirection : 'asc'}
                          onClick={() => handleSort('assignedBy')}
                        >
                          {localeProcesses['assignedBy']}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '12%' }}>
                        <TableSortLabel
                          active={sortField === 'date'}
                          direction={sortField === 'date' ? sortDirection : 'asc'}
                          onClick={() => handleSort('date')}
                        >
                          {localeCommon['date']}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, width: '8%' }}>
                        <TableSortLabel
                          active={sortField === 'duration'}
                          direction={sortField === 'duration' ? sortDirection : 'asc'}
                          onClick={() => handleSort('duration')}
                        >
                          {localeProcesses['duration']}
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedWatchlistExecutions.map((execution) => (
                      <React.Fragment key={execution.processExecutionId}>
                        <TableRow hover>
                          <TableCell>
                            {execution?.status !== 'ASSIGNED' ? (
                              <IconButton
                                size="small"
                                onClick={() => handleWatchlistAccordionChange(execution?.processExecutionId || '')}
                              >
                                {expandedWatchlistExecution === execution?.processExecutionId ? (
                                  <KeyboardArrowUp />
                                ) : (
                                  <KeyboardArrowDown />
                                )}
                              </IconButton>
                            ) : (
                              <IconButton size="small" disabled>
                                <KeyboardArrowDown className="opacity-30" />
                              </IconButton>
                            )}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 0 }}>
                            <Tooltip title={execution?.processSnapshot?.name || execution?.process?.name || '-'} arrow>
                              <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                <WolfTextLink onClick={() => history.push(`/processes/${execution.processId}`)}>
                                  {execution?.processSnapshot?.name || execution?.process?.name || '-'}
                                </WolfTextLink>
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Box className="flex items-center gap-2">
                              <Chip
                                label={formatStatus(execution?.status || '')}
                                color={getStatusColor(execution?.status || '')}
                                size="small"
                              />
                              {execution?.assignerNote && (
                                <Tooltip title={execution.assignerNote} arrow>
                                  <ChatBubbleOutline sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                                </Tooltip>
                              )}
                              {!execution?.isRedacted &&
                                execution?.status === 'IN_PROGRESS' && (
                                  <Box className="flex gap-1">
                                    {(execution?.startedByUser?.userId === userId) && (
                                      <Tooltip title={localeProcesses['continueExecution']}>
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          onClick={() => handleStartExecution(execution.processId)}
                                        >
                                          <PlayArrow fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    <Tooltip title={localeProcesses['markStuckExecutionAsCompletedTooltip']}>
                                      <IconButton
                                        size="small"
                                        color="success"
                                        onClick={() =>
                                          handleUpdateExecutionStatus(execution?.processExecutionId || '', 'COMPLETED')
                                        }
                                      >
                                        <CheckCircle fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title={localeProcesses['markStuckExecutionAsFailedTooltip']}>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() =>
                                          handleUpdateExecutionStatus(execution?.processExecutionId || '', 'FAILED')
                                        }
                                      >
                                        <Error fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title={localeProcesses['markStuckExecutionAsCancelledTooltip']}>
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          handleUpdateExecutionStatus(execution?.processExecutionId || '', 'CANCELLED')
                                        }
                                      >
                                        <Cancel fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                )}
                              {!execution?.isRedacted &&
                                execution?.status === 'ASSIGNED' &&
                                execution?.startedByUser?.userId === userId && (
                                  <Box className="flex gap-1">
                                    <Tooltip title={localeProcesses['startAssignedExecution']}>
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => handleStartExecution(execution.processId)}
                                      >
                                        <PlayArrow fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title={localeProcesses['skip']}>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleUpdateExecutionStatus(execution?.processExecutionId || '', 'SKIPPED')}
                                      >
                                        <SkipNext fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {execution?.startedByUser ? (
                              <Tooltip title={execution.startedByUser.email || ''} arrow>
                                <span>
                                  {execution.startedByUser.firstName || execution.startedByUser.lastName
                                    ? `${execution.startedByUser.firstName || ''} ${execution.startedByUser.lastName || ''}`.trim()
                                    : execution.startedByUser.email || '-'}
                                </span>
                              </Tooltip>
                            ) : '-'}
                          </TableCell>
                          <TableCell>
                            {execution?.assignedByUser ? (
                              <Tooltip title={execution.assignedByUser.email || ''} arrow>
                                <span>
                                  {execution.assignedByUser.firstName || execution.assignedByUser.lastName
                                    ? `${execution.assignedByUser.firstName || ''} ${execution.assignedByUser.lastName || ''}`.trim()
                                    : execution.assignedByUser.email || '-'}
                                </span>
                              </Tooltip>
                            ) : '-'}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {execution?.createdAt
                              ? DateTime.fromISO(execution.createdAt).toFormat('dd-MM-yyyy HH:mm')
                              : '-'}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {execution?.startedAt ? calculateDuration(execution.startedAt, execution?.completedAt) : '-'}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                            <Collapse in={expandedWatchlistExecution === execution?.processExecutionId} timeout="auto" unmountOnExit>
                              <Box className="py-4">
                                {execution?.stepExecutions && execution?.stepExecutions?.length > 0 ? (
                                  <Box className="flex flex-col gap-1">
                                    <Typography variant="caption" className="font-semibold text-gray-500 tracking-wide mb-2">
                                      {localeCommon['allSteps']}
                                    </Typography>
                                    {sortStepExecutions(execution?.stepExecutions)
                                      ?.map((step, index) => (
                                        <Box
                                          key={step?.stepExecutionId}
                                          className="flex flex-col gap-1 py-2 px-3 bg-gray-50 rounded-md border-l-2 border-gray-200"
                                          sx={{
                                            borderLeftColor: step?.status === 'COMPLETED' ? 'success.main' :
                                              step?.status === 'FAILED' ? 'error.main' :
                                              step?.status === 'SKIPPED' ? 'warning.main' : 'grey.300'
                                          }}
                                        >
                                          <Box className="flex items-center gap-2">
                                            <Typography
                                              variant="caption"
                                              className="text-gray-400 font-medium min-w-[20px]"
                                            >
                                              {index + 1}.
                                            </Typography>
                                            <Box className="flex items-center">
                                              {getStepStatusIcon(step?.status || StepExecutionStatus.PENDING)}
                                            </Box>
                                            <Typography variant="body2" className="flex-1 font-medium text-gray-700">
                                              {step?.stepSnapshot?.name || '-'}
                                            </Typography>
                                            <Chip
                                              label={formatStatus(step?.status || '')}
                                              color={getStatusColor(step?.status || '')}
                                              size="small"
                                              sx={{ height: 20, fontSize: '0.7rem' }}
                                            />
                                          </Box>
                                          {step?.notes && (
                                            <Box className="ml-7 mt-1">
                                              <Typography variant="caption" className="text-gray-500">
                                                {localeProcesses['notes']}: {step.notes}
                                              </Typography>
                                            </Box>
                                          )}
                                        </Box>
                                      ))}
                                  </Box>
                                ) : (
                                  <Typography variant="body2" className="text-gray-400 italic">
                                    {localeProcesses['noSteps']}
                                  </Typography>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filteredWatchlistExecutions.length}
                page={watchlistPage}
                onPageChange={handleWatchlistChangePage}
                rowsPerPage={watchlistRowsPerPage}
                onRowsPerPageChange={handleWatchlistChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage={localeCommon['rowsPerPage']}
              />
              </>
              )}
          </Box>
        )}

        {/* Tab 3: All Executions */}
        {((useTabsView && activeTab === 3) || !useTabsView) && (
          <Box>
            <Box className="flex items-center gap-1 mb-2">
              <Typography variant="subtitle1" className="text-textSecondary font-semibold tracking-wide text-sm">
                {localeProcesses['allExecutions']}
              </Typography>
              <Tooltip title={localeProcesses['allExecutionsTooltip']} arrow placement="right">
                <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Box>
            <ExecutionsFilterToolbar
              filterState={filterState}
              onFilterChange={handleFilterChange}
              availableProcesses={availableProcesses}
              availableAreas={availableAreas}
              availableServices={availableServices}
              dateFilterFirst={true}
            />

        {loading ? (
          <Box className="flex items-center justify-center py-12">
            <CircularProgress />
          </Box>
        ) : executions.length === 0 && myPendingExecutions.length === 0 ? (
          <Box className="flex flex-col items-center justify-center py-12 text-center">
            <Typography variant="h6" className="text-gray-500 font-medium">
              {localeProcesses['noExecutionsYet']}
            </Typography>
            <Typography variant="body2" className="text-gray-400 mt-1">
              {localeProcesses['startExecutingProcesses']}
            </Typography>
          </Box>
        ) : filteredExecutions.length === 0 ? (
          <Box className="flex flex-col items-center justify-center py-8 text-center">
            <Typography variant="body1" className="text-gray-500">
              {localeProcesses['noExecutionsMatchingFilters']}
            </Typography>
          </Box>
        ) : (
          <>
          {/* Simple table view of all executions */}
          <TableContainer component={Paper} elevation={1} className="rounded-lg overflow-hidden mt-4">
            <Table size="small" sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.200' }}>
                  <TableCell sx={{ width: '3%', padding: '0 4px' }} />
                  <TableCell sx={{ fontWeight: 600, width: '18%' }}>
                    <TableSortLabel
                      active={sortField === 'process'}
                      direction={sortField === 'process' ? sortDirection : 'asc'}
                      onClick={() => handleSort('process')}
                    >
                      {localeCommon['process']}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, width: '20%' }}>
                    <TableSortLabel
                      active={sortField === 'status'}
                      direction={sortField === 'status' ? sortDirection : 'asc'}
                      onClick={() => handleSort('status')}
                    >
                      {localeCommon['status']}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, width: '15%' }}>
                    <TableSortLabel
                      active={sortField === 'startedBy'}
                      direction={sortField === 'startedBy' ? sortDirection : 'asc'}
                      onClick={() => handleSort('startedBy')}
                    >
                      {localeProcesses['startedByAssignedTo']}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, width: '12%', whiteSpace: 'nowrap' }}>
                    <TableSortLabel
                      active={sortField === 'assignedBy'}
                      direction={sortField === 'assignedBy' ? sortDirection : 'asc'}
                      onClick={() => handleSort('assignedBy')}
                    >
                      {localeProcesses['assignedBy']}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, width: '12%' }}>
                    <TableSortLabel
                      active={sortField === 'date'}
                      direction={sortField === 'date' ? sortDirection : 'asc'}
                      onClick={() => handleSort('date')}
                    >
                      {localeCommon['date']}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, width: '8%' }}>
                    <TableSortLabel
                      active={sortField === 'duration'}
                      direction={sortField === 'duration' ? sortDirection : 'asc'}
                      onClick={() => handleSort('duration')}
                    >
                      {localeProcesses['duration']}
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedFilteredExecutions.map((execution) => (
                    <React.Fragment key={execution.processExecutionId}>
                      <TableRow hover>
                        <TableCell>
                          {execution?.status !== 'ASSIGNED' ? (
                            <IconButton
                              size="small"
                              onClick={() => handleAllAccordionChange(execution?.processExecutionId || '')}
                            >
                              {expandedAllExecution === execution?.processExecutionId ? (
                                <KeyboardArrowUp />
                              ) : (
                                <KeyboardArrowDown />
                              )}
                            </IconButton>
                          ) : (
                            <IconButton size="small" disabled>
                              <KeyboardArrowDown className="opacity-30" />
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 0 }}>
                          <Tooltip title={execution?.processSnapshot?.name || '-'} arrow>
                            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              <WolfTextLink onClick={() => history.push(`/processes/${execution.processId}`)}>
                                {execution?.processSnapshot?.name || '-'}
                              </WolfTextLink>
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Box className="flex items-center gap-2">
                            <Chip
                              label={formatStatus(execution?.status || '')}
                              color={getStatusColor(execution?.status || '')}
                              size="small"
                            />
                            {execution?.assignerNote && (
                              <Tooltip title={execution.assignerNote} arrow>
                                <ChatBubbleOutline sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                              </Tooltip>
                            )}
                            {execution?.status === 'IN_PROGRESS' && (
                                <Box className="flex gap-1">
                                  {execution?.startedByUser?.userId === userId && (
                                    <Tooltip title={localeProcesses['continueExecution']}>
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => handleStartExecution(execution.processId)}
                                      >
                                        <PlayArrow fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  <Tooltip title={localeProcesses['markStuckExecutionAsCompletedTooltip']}>
                                    <IconButton
                                      size="small"
                                      color="success"
                                      onClick={() =>
                                        handleUpdateExecutionStatus(execution?.processExecutionId || '', 'COMPLETED')
                                      }
                                    >
                                      <CheckCircle fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={localeProcesses['markStuckExecutionAsFailedTooltip']}>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() =>
                                        handleUpdateExecutionStatus(execution?.processExecutionId || '', 'FAILED')
                                      }
                                    >
                                      <Error fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={localeProcesses['markStuckExecutionAsCancelledTooltip']}>
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleUpdateExecutionStatus(execution?.processExecutionId || '', 'CANCELLED')
                                      }
                                    >
                                      <Cancel fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              )}
                            {execution?.status === 'ASSIGNED' &&
                              execution?.startedByUser?.userId === userId && (
                                <Box className="flex gap-1">
                                  <Tooltip title={localeProcesses['startAssignedExecution']}>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => handleStartExecution(execution.processId)}
                                    >
                                      <PlayArrow fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={localeProcesses['skip']}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleUpdateExecutionStatus(execution?.processExecutionId || '', 'SKIPPED')}
                                    >
                                      <SkipNext fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {execution?.startedByUser ? (
                            <Tooltip title={execution.startedByUser.email || ''} arrow>
                              <span>
                                {execution.startedByUser.firstName || execution.startedByUser.lastName
                                  ? `${execution.startedByUser.firstName || ''} ${execution.startedByUser.lastName || ''}`.trim()
                                  : execution.startedByUser.email || '-'}
                              </span>
                            </Tooltip>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {execution?.assignedByUser ? (
                            <Tooltip title={execution.assignedByUser.email || ''} arrow>
                              <span>
                                {execution.assignedByUser.firstName || execution.assignedByUser.lastName
                                  ? `${execution.assignedByUser.firstName || ''} ${execution.assignedByUser.lastName || ''}`.trim()
                                  : execution.assignedByUser.email || '-'}
                              </span>
                            </Tooltip>
                          ) : '-'}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {execution?.createdAt
                            ? DateTime.fromISO(execution.createdAt).toFormat('dd-MM-yyyy HH:mm')
                            : '-'}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {execution?.startedAt ? calculateDuration(execution.startedAt, execution?.completedAt) : '-'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                          <Collapse in={expandedAllExecution === execution?.processExecutionId} timeout="auto" unmountOnExit>
                            <Box className="py-4">
                              {execution?.stepExecutions && execution?.stepExecutions?.length > 0 ? (
                                <Box className="flex flex-col gap-1">
                                  <Typography variant="caption" className="font-semibold text-gray-500 tracking-wide mb-2">
                                    {localeCommon['allSteps']}
                                  </Typography>
                                  {sortStepExecutions(execution?.stepExecutions)
                                    ?.map((step, index) => (
                                      <Box
                                        key={step?.stepExecutionId}
                                        className="flex flex-col gap-1 py-2 px-3 bg-gray-50 rounded-md border-l-2 border-gray-200"
                                        sx={{
                                          borderLeftColor: step?.status === 'COMPLETED' ? 'success.main' :
                                            step?.status === 'FAILED' ? 'error.main' :
                                            step?.status === 'SKIPPED' ? 'warning.main' : 'grey.300'
                                        }}
                                      >
                                        <Box className="flex items-center gap-2">
                                          <Typography
                                            variant="caption"
                                            className="text-gray-400 font-medium min-w-[20px]"
                                          >
                                            {index + 1}.
                                          </Typography>
                                          <Box className="flex items-center">
                                            {getStepStatusIcon(step?.status || StepExecutionStatus.PENDING)}
                                          </Box>
                                          <Typography variant="body2" className="flex-1 font-medium text-gray-700">
                                            {step?.stepSnapshot?.name || '-'}
                                          </Typography>
                                          <Chip
                                            label={formatStatus(step?.status || '')}
                                            color={getStatusColor(step?.status || '')}
                                            size="small"
                                            sx={{ height: 20, fontSize: '0.7rem' }}
                                          />
                                        </Box>
                                        {step?.errorDetails?.message && (
                                          <Box className="ml-7 mt-1">
                                            <Typography variant="caption" className="text-error">
                                              {localeProcesses['errorDetails']}: {step.errorDetails.message}
                                            </Typography>
                                          </Box>
                                        )}
                                        {step?.stepSnapshot?.stepData?.condition && (
                                          <Box className="ml-7 mt-1">
                                            <Typography variant="caption" className="text-feedbackWarning font-semibold">
                                              {localeProcesses['condition']}: {step.stepSnapshot.stepData.condition}
                                            </Typography>
                                          </Box>
                                        )}
                                        {step?.notes && (
                                          <Box className="ml-7 mt-1">
                                            <Typography variant="caption" className="text-gray-500">
                                              {localeProcesses['notes']}: {step.notes}
                                            </Typography>
                                          </Box>
                                        )}
                                        {step?.confirmationResponse && step?.stepSnapshot?.stepData?.confirmationType && (
                                          <Box className="ml-7 mt-1">
                                            <Typography variant="caption" className="text-primary font-semibold">
                                              {step.stepSnapshot.stepData.confirmationType === ConfirmationType.BOOLEAN
                                                ? `✓ ${localeProcesses['confirmed']}`
                                                : `${step.stepSnapshot.stepData.confirmationQuestion || localeProcesses['confirmationResponse']}: ${step.confirmationResponse}`}
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>
                                    ))}
                                </Box>
                              ) : (
                                <Typography variant="body2" className="text-gray-400 italic">
                                  {localeProcesses['noSteps']}
                                </Typography>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 20, 50]}
            component="div"
            count={filteredExecutions.length}
            rowsPerPage={allExecutionsRowsPerPage}
            page={allExecutionsPage}
            onPageChange={handleAllExecutionsChangePage}
            onRowsPerPageChange={handleAllExecutionsChangeRowsPerPage}
          />
          </>
        )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ExecutionsList;
